/**
 * shipDropOff.ts — a seller's (or a trade buyer's) drop-off code and "I've
 * Dropped It Off", on the website's tracking pop-up.
 *
 * WHAT WAS WRONG. The pop-up's QR panel never showed (floorTransport mapLeg
 * keeps `qr` false on purpose: Blake's panel draws a GENERATED qrSVG, which
 * would not scan at a counter), and nothing on the page could record a
 * drop-off. A seller whose buyer paid in the app therefore could not get the
 * code at all from the website (Blake ruled YES 2026-10-07, card
 * 1219143151168430).
 *
 * WHICH ROUTES. The ones the app's own Ship screen uses (QrDropOffScreen.tsx):
 *
 *   code  GET  /api/shippo/label-qr/:offerId    { side, qrCodeUrl, trackingNumber, carrier }
 *   drop  POST /api/orders/:offerId/ship        seller   -> { ok: true }
 *         POST /api/orders/:offerId/buyer-ship  trade buyer
 *
 * 🔴 NOT GET /api/orders/:offerId/qr. That route BUYS a Shippo label on its
 * first call when none exists (platform money), answers a refusal with a
 * machine word ("waiting_on_other_party") rather than a sentence, and returns
 * the STORED code, which Shippo signs with an expiry. The app never calls it
 * (offers.ts ensureCashLabelPurchased documents why). label-qr is read-only,
 * re-fetches a fresh signed URL, and 404s with a sentence while no label
 * exists, so a tap here can never spend anything. One constant below if the
 * choice is ever reversed.
 *
 * WHEN THE CODE IS FETCHED. Only from a tap on "Show drop-off QR code" inside
 * the pop-up, by the person whose leg it is (the button is painted only on
 * their own leg, see DemoTrackingLeg.ship). Never on page load, never when the
 * deal list or thread renders, never from the poll, never from a deal signal.
 * A second tap while the first is in flight does nothing, and closing or
 * reopening the pop-up forgets the code, so each open costs one fetch at most
 * (a signed code expires, and every fetch is logged server-side).
 *
 * WHAT IS SHOWN. The URL the server returned, in an <img>, and nothing drawn
 * here: only an https URL is accepted, anything else is treated as no code.
 *
 * THE BUTTON FOLLOWS THE IMAGE. "I've Dropped It Off" is painted only once the
 * <img> has fired `load`. If the browser cannot load it (a blocked host, an
 * expired signed URL) the panel says "Could not load QR code" and offers no
 * drop-off: nobody confirms a drop-off they could not scan. The listeners are
 * one capture pair on the document (script-src-attr is 'none', so there are no
 * inline handlers), and they read the image's own data-ship* attributes.
 *
 * ONE POST PER LEG. The keys of POSTs still in flight live in their own set,
 * which closing or reopening the pop-up never touches, so a reopened pop-up
 * cannot start a second POST while the first is unanswered (the server route
 * re-stamps shippedAt and signals again). They clear when the POST settles.
 *
 * SUCCESS IS THE SERVER'S. "Package Dropped Off!" appears only after the POST
 * answered 2xx with { ok: true } (a 200 HTML page is not a success). A refusal (400/403/409 ...) shows the server's own sentence and
 * leaves the button where it was; the deal list and thread are re-read after a
 * success so the page shows what the server now says.
 *
 * No GUARDED_WRITES entry is touched: Blake's page has no handler for any of
 * these controls (they carry their own data-shipact), so there is no
 * fabricating click handler beneath them to guard.
 */

import { installDealThread, refreshDeals } from "./floorTransport";

export const SHIP_ACTION = "[data-shipact]";

/** The app's own words for a failure the server gave no sentence for
 *  (QrDropOffScreen.tsx: 'Could not load QR code' / 'Failed to confirm shipment'). */
export const CODE_UNAVAILABLE_MESSAGE = "Could not load QR code";
export const DROP_FAILURE_MESSAGE = "Failed to confirm shipment";

/** Reversal point: see the header. */
export const codeEndpoint = (offerId: string): string => `/api/shippo/label-qr/${encodeURIComponent(offerId)}`;
export const shipEndpoint = (offerId: string, side: ShipSide): string =>
  `/api/orders/${encodeURIComponent(offerId)}/${side === "seller" ? "ship" : "buyer-ship"}`;

export type ShipSide = "seller" | "buyer";

/** What app.js's renderShipPanel paints from. A copy, never the live object. */
export interface ShipView {
  code: "idle" | "loading" | "ready" | "error";
  qrUrl: string | null;
  carrier: string | null;
  trackingNumber: string | null;
  codeError: string | null;
  drop: "idle" | "asking" | "posting" | "done";
  dropError: string | null;
  /** Whether the browser has loaded the code image; the drop button waits for it. */
  img: "pending" | "loaded";
}

type ShipWindow = Window & {
  render?: () => void;
  rerenderKeepScroll?: () => void;
  __gbzShipLeg?: (offerId: string, side: ShipSide) => ShipView;
};

const keyOf = (offerId: string, side: ShipSide): string => `${side}:${offerId}`;
const IDLE: ShipView = {
  code: "idle", qrUrl: null, carrier: null, trackingNumber: null, codeError: null, drop: "idle", dropError: null, img: "pending",
};

const textOrNull = (v: unknown): string | null => (typeof v === "string" && v ? v : null);

/** The body as an object, or null: a refusal can arrive as an HTML error page. */
async function bodyOf(res: Response): Promise<Record<string, unknown> | null> {
  const body: unknown = await res.json().catch(() => null);
  return body && typeof body === "object" ? (body as Record<string, unknown>) : null;
}

export function installShipDropOff(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  myUserId: string,
): () => void {
  const w = win as ShipWindow;
  const views = new Map<string, ShipView>();
  /* Drop-offs the server accepted this session. A re-read normally carries the
     same fact (shippedAt), but the pop-up must not offer the button again in
     the gap before it lands. */
  const droppedKeys = new Set<string>();
  /* Ship POSTs not yet answered. views.clear() must never touch this: it is the
     only thing that stops a reopened pop-up from posting a second time. */
  const inflight = new Set<string>();
  /* Bumped whenever the pop-up opens or closes, so a code that arrives after
     that is dropped instead of appearing in a pop-up the person re-opened. */
  let generation = 0;

  const get = (key: string): ShipView => views.get(key) ?? { ...IDLE };
  const set = (key: string, patch: Partial<ShipView>): void => {
    views.set(key, { ...get(key), ...patch });
    if (typeof w.rerenderKeepScroll === "function") w.rerenderKeepScroll();
    else w.render?.();
  };

  w.__gbzShipLeg = (offerId, side) => {
    const key = keyOf(offerId, side);
    const v = get(key);
    if (droppedKeys.has(key)) return { ...v, drop: "done" };
    if (inflight.has(key)) return { ...v, drop: "posting" };
    return { ...v };
  };

  async function showCode(key: string, offerId: string): Promise<void> {
    const mine = generation;
    set(key, { code: "loading", codeError: null });
    let view: Partial<ShipView>;
    try {
      const res = await authFetch(codeEndpoint(offerId));
      const body = await bodyOf(res);
      if (!res.ok) {
        view = { code: "error", codeError: textOrNull(body?.error) ?? CODE_UNAVAILABLE_MESSAGE };
      } else {
        const url = textOrNull(body?.qrCodeUrl);
        view =
          url && /^https:\/\//i.test(url)
            ? { code: "ready", qrUrl: url, img: "pending", carrier: textOrNull(body?.carrier), trackingNumber: textOrNull(body?.trackingNumber) }
            : { code: "error", codeError: CODE_UNAVAILABLE_MESSAGE };
      }
    } catch {
      view = { code: "error", codeError: CODE_UNAVAILABLE_MESSAGE };
    }
    if (mine !== generation) return;
    set(key, view);
  }

  async function postDrop(key: string, offerId: string, side: ShipSide, threadId: string): Promise<void> {
    let res: Response;
    let body: Record<string, unknown> | null;
    try {
      res = await authFetch(shipEndpoint(offerId, side), {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({}),
      });
      body = await bodyOf(res);
    } catch {
      inflight.delete(key);
      set(key, { drop: "idle", dropError: DROP_FAILURE_MESSAGE });
      return;
    }
    /* Settled: cleared BEFORE the repaint below, which reads it. */
    inflight.delete(key);
    if (!res.ok || body?.ok !== true) {
      set(key, { drop: "idle", dropError: textOrNull(body?.error) ?? DROP_FAILURE_MESSAGE });
      return;
    }
    droppedKeys.add(key);
    set(key, { drop: "done", dropError: null });
    /* Best-effort, like dealActions: the drop-off has landed, so a failed
       re-read costs a repaint and never the success. */
    await refreshDeals(win, authFetch, myUserId).catch(() => null);
    if (threadId) await installDealThread(win, authFetch, threadId, myUserId).catch(() => false);
  }

  const onClick = (event: Event): void => {
    const target = event.target as Element | null;
    if (!target?.closest) return;

    /* Opening or closing the pop-up forgets any code and any half-made
       confirmation, so a reopened pop-up asks again. The same taps still reach
       app.js, which opens and closes it. */
    if (
      target.closest("[data-trackopen]") ||
      target.closest("#track-close") ||
      target.id === "track-back" ||
      (target.closest("[data-threadcta]") as HTMLElement | null)?.dataset.threadcta === "View QR drop-off label"
    ) {
      generation++;
      views.clear();
      return;
    }

    const el = target.closest(SHIP_ACTION) as HTMLElement | null;
    if (!el) return;
    const offerId = el.dataset.shipoffer;
    const side = el.dataset.shipside;
    if (!offerId || (side !== "seller" && side !== "buyer")) return;
    const key = keyOf(offerId, side);
    const view = get(key);
    const act = el.dataset.shipact;

    if (act === "show") {
      if (view.code === "loading" || view.code === "ready") return;
      void showCode(key, offerId);
    } else if (act === "drop") {
      if (view.code === "ready" && view.img === "loaded" && view.drop === "idle" && !droppedKeys.has(key) && !inflight.has(key)) {
        set(key, { drop: "asking", dropError: null });
      }
    } else if (act === "no") {
      if (view.drop === "asking") set(key, { drop: "idle" });
    } else if (act === "yes") {
      if (view.drop !== "asking" || inflight.has(key)) return;
      inflight.add(key);
      set(key, { drop: "posting", dropError: null });
      void postDrop(key, offerId, side, el.dataset.shipthread ?? "");
    }
  };

  /* The code image's own outcome. `load` and `error` do not bubble, so this is
     a capture listener; the image is found by the data-ship* attributes
     renderShipPanel gives it, and only the CURRENT url of an open code counts
     (a stale image from a closed pop-up changes nothing). A repaint replaces
     the <img>, so a second `load` for a view already marked loaded is ignored:
     acting on it would repaint again, forever. */
  const onImage = (event: Event): void => {
    const img = event.target as HTMLElement | null;
    if (!img || img.tagName !== "IMG") return;
    const offerId = img.dataset.shipoffer;
    const side = img.dataset.shipside;
    if (!offerId || (side !== "seller" && side !== "buyer")) return;
    const key = keyOf(offerId, side);
    const view = get(key);
    if (view.code !== "ready" || view.qrUrl !== img.getAttribute("src")) return;
    if (event.type === "load") {
      if (view.img !== "loaded") set(key, { img: "loaded" });
    } else if (view.drop !== "posting") {
      set(key, { code: "error", codeError: CODE_UNAVAILABLE_MESSAGE, qrUrl: null, img: "pending", drop: "idle", dropError: null });
    }
  };
  win.document.addEventListener("load", onImage, true);
  win.document.addEventListener("error", onImage, true);

  /* Capture, never stopping propagation, like every other module here. */
  win.document.addEventListener("click", onClick, true);
  const onHash = (): void => {
    generation++;
    views.clear();
  };
  win.addEventListener("hashchange", onHash);
  return () => {
    win.document.removeEventListener("click", onClick, true);
    win.document.removeEventListener("load", onImage, true);
    win.document.removeEventListener("error", onImage, true);
    win.removeEventListener("hashchange", onHash);
    delete w.__gbzShipLeg;
  };
}
